import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import { useI18n } from '../i18n';
import { isPdf, isVideo } from './MediaStrip';
import { fmtMoney } from '../lib/format';

// One line of an estimate, edited on its own screen — modelled on Joist's
// Item Details.
//
// It replaced a five-column inline grid where the description was a ~90px
// input. That was unusable on a phone for anything past a couple of words,
// which is the opposite of what a line item is for: the tech describing what
// they found and what it takes to fix it.
//
// Photos attach to the line rather than the estimate, so a line is a record of
// the work itself — what was found, or what was finished — and not just a
// price. They upload as they're picked, so submitting the estimate stays a
// plain JSON post carrying URLs.

// `variant` is which document the line belongs to. 'tech' is the original: a
// technician's estimate line, which is labour or materials and whose total is
// quantity times rate. 'doc' is a line on Julian's own estimate or invoice,
// where labour/materials is not a question the customer's paper asks, and
// where a fixed price with no workings behind it is the normal case — so the
// price is typed, and quantity × rate fills it in when both are given.
// Anything that is neither a picture nor a clip. Cloudinary hands documents
// back down the same /image/ path it uses for photos, so the URL says nothing
// and only the filename does.
const DOC_EXT = /\.(pdf|docx?|xlsx?|csv|txt|rtf|pages|numbers)$/i;
const isDocFile = (f) => DOC_EXT.test(String(f?.name || '')) || /\.(docx?|xlsx?|csv|txt|rtf|pages|numbers)(\?|$)/i.test(String(f?.url || ''));

// 2.4 MB. A tech deciding whether a file uploaded needs to see something other
// than a filename they already knew.
const fmtBytes = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${Math.round(b / 1024)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export default function ItemEditor({ item, index, total, onChange, onDelete, onClose, variant = 'tech', backLabel }) {
  const { t } = useI18n();
  // The app's own uploader, which signs the request. This screen used to post
  // to /api/uploads itself with no Authorization header, so every attachment
  // came back "No token provided" — a protected route answering exactly as it
  // should to a request that forgot to say who it was from.
  const { uploadDocuments } = useApp();
  const [busy, setBusy] = useState(false);
  const isDoc = variant === 'doc';
  const set = (patch) => {
    const next = { ...item, ...patch };
    // Only compute when both are there: a lone quantity would otherwise wipe a
    // price he typed on purpose. Same rule the old estimate rows followed.
    if (isDoc && ('qty' in patch || 'unitCost' in patch)) {
      const q = parseFloat(next.qty), r = parseFloat(next.unitCost);
      if (!isNaN(q) && !isNaN(r)) next.price = String(Math.round(q * r * 100) / 100);
    }
    onChange(next);
  };

  const lineTotal = isDoc
    ? (parseFloat(item.price) || 0)
    : (parseFloat(item.qty) || 0) * (parseFloat(item.unitCost) || 0);

  // Pictures and documents go to the same place and land in different lists.
  // A photo is looked at and a file is opened, and a PDF dropped into the
  // photo strip draws as a broken image — so what comes back is sorted by
  // what it is, not by which button was pressed.
  const addAttachments = async (picked) => {
    if (!picked.length) return;
    setBusy(true);
    try {
      const got = await uploadDocuments(picked);
      const pics = got.filter(f => !isPdf(f.url) && !isVideo(f.url) && !isDocFile(f));
      const docs = got.filter(f => isPdf(f.url) || isVideo(f.url) || isDocFile(f));
      const patch = {};
      if (pics.length) patch.photos = [...(item.photos || []), ...pics.map(f => f.url)];
      if (docs.length) patch.files = [...(item.files || []), ...docs.map(f => ({ url: f.url, name: f.name, bytes: f.bytes }))];
      if (pics.length || docs.length) set(patch);
    } finally {
      setBusy(false);
    }
  };

  const DESC_MAX = 500;
  const step = (by) => {
    const q = Math.max(0, Math.round(((parseFloat(item.qty) || 0) + by) * 100) / 100);
    set({ qty: String(q) });
  };

  return (
    <>
      <div className="modal active ie-modal" style={{ alignItems: 'stretch' }}>
        <div className="item-editor ie">
          <div className="ie-bar">
            <button className="ie-bar-btn" onClick={onClose}>‹ {backLabel || t('ie.items')}</button>
            <span className="ie-bar-n">{index + 1} / {total}</span>
            <button className="ie-bar-btn strong" onClick={onClose}>{t('ie.done')}</button>
          </div>

          <div className="ie-body">
            <h2 className="ie-title">{t('ie.itemDetails')}</h2>

            <section className="ie-card">
              {/* Two labels and two boxes. It carried an example in each
                  placeholder and a paragraph explaining the bullets, and the
                  examples read as text someone had already typed — a tech
                  seeing "e.g. Drywall repair" had to work out whether it was a
                  suggestion or their own words. The bullets still work: one job
                  per line, and a line starting "Note:" sets in italic. */}
              <label className="ie-label" htmlFor="ie-name">Item name</label>
              <input
                id="ie-name"
                className="ie-input"
                value={item.name || ''}
                onChange={e => set({ name: e.target.value })}
              />

              <label className="ie-label" htmlFor="ie-desc">Item description</label>
              <textarea
                id="ie-desc"
                className="ie-textarea"
                value={item.description || ''}
                maxLength={DESC_MAX}
                onChange={e => set({ description: e.target.value })}
                rows={6}
              />
              <div className="ie-count">{(item.description || '').length}/{DESC_MAX}</div>
            </section>

            <section className="ie-card">
              <div className="ie-label">Photos &amp; files <span className="ie-opt">(optional)</span></div>
              <p className="ie-hint">Show the work, the product, or the paperwork behind it. Everything attaches to this line, not the whole estimate.</p>

              {/* Three ways in, because they are three different things on a
                  phone: the camera right now, the roll, or a file. `capture`
                  is what opens the camera directly instead of the picker. */}
              <div className="ie-photo-btns">
                <label className="ie-photo-btn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8.5h3l1.6-2.4h8.8L18 8.5h3v11H3z" /><circle cx="12" cy="13.5" r="3.4" /></svg>
                  <span>Take photo</span>
                  <input type="file" accept="image/*" capture="environment" hidden disabled={busy}
                    onChange={e => { addAttachments(Array.from(e.target.files)); e.target.value = ''; }} />
                </label>
                <label className="ie-photo-btn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="8.5" cy="10" r="1.6" /><path d="M21 16l-5-5-5 5-2.5-2.5L3 18" /></svg>
                  <span>Gallery</span>
                  <input type="file" accept="image/*" multiple hidden disabled={busy}
                    onChange={e => { addAttachments(Array.from(e.target.files)); e.target.value = ''; }} />
                </label>
                <label className="ie-photo-btn">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M14 3.5H6.5A1.5 1.5 0 005 5v14a1.5 1.5 0 001.5 1.5h11A1.5 1.5 0 0019 19V8.5z" /><path d="M14 3.5V9h5" /></svg>
                  <span>Files</span>
                  <input type="file" multiple hidden disabled={busy}
                    onChange={e => { addAttachments(Array.from(e.target.files)); e.target.value = ''; }} />
                </label>
              </div>
              {busy && <p className="ie-hint" style={{ marginTop: 10 }}>{t('req.submitting')}</p>}

              {(item.photos || []).length > 0 && (
                <div className="photo-strip" style={{ marginTop: 12 }}>
                  {item.photos.map((url, i) => (
                    <div key={i} style={{ position: 'relative', flexShrink: 0 }}>
                      <img src={url} alt="" className="photo-thumb" onClick={() => window.open(url, '_blank')} />
                      <button className="item-editor-x" aria-label={t('req.cancel')}
                        onClick={() => set({ photos: item.photos.filter((_, idx) => idx !== i) })}>×</button>
                    </div>
                  ))}
                </div>
              )}

              {(item.files || []).length > 0 && (
                <ul className="ie-files">
                  {item.files.map((f, i) => (
                    <li key={i}>
                      <a href={f.url} target="_blank" rel="noopener noreferrer">
                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M14 3.5H6.5A1.5 1.5 0 005 5v14a1.5 1.5 0 001.5 1.5h11A1.5 1.5 0 0019 19V8.5z" /><path d="M14 3.5V9h5" /></svg>
                        <span className="ie-file-n">{f.name || 'Attachment'}</span>
                        {f.bytes ? <em>{fmtBytes(f.bytes)}</em> : null}
                      </a>
                      <button type="button" className="ie-file-x" aria-label={`Remove ${f.name || 'this file'}`}
                        onClick={() => set({ files: item.files.filter((_, idx) => idx !== i) })}>×</button>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section className="ie-card">
              {/* Labor or materials used to be the first thing on this card.
                  It is gone, at Julian's word: his technicians write one line
                  per piece of work and the split was a question they answered
                  wrong as often as right. The field is still on the record,
                  so lines already marked keep what they say and nothing a
                  technician typed is lost — nothing reads it. */}
              <div className="ie-qr">
                <div>
                  <label className="ie-label" htmlFor="ie-qty">Quantity</label>
                  <div className="ie-stepper">
                    <button type="button" onClick={() => step(-1)} aria-label="One less">−</button>
                    <input id="ie-qty" type="number" inputMode="decimal" min="0" step="any"
                      value={item.qty} onChange={e => set({ qty: e.target.value })} placeholder="1" />
                    <button type="button" onClick={() => step(1)} aria-label="One more">+</button>
                  </div>
                </div>
                <div>
                  <label className="ie-label" htmlFor="ie-rate">Rate (USD)</label>
                  <div className="ie-money">
                    <span>$</span>
                    <input id="ie-rate" type="number" inputMode="decimal" min="0" step="0.01"
                      value={item.unitCost} onChange={e => set({ unitCost: e.target.value })} placeholder="0.00" />
                  </div>
                </div>
              </div>

              {isDoc && (
                <p className="ie-hint" style={{ marginTop: 12 }}>
                  Quantity and rate are optional workings — fill both in and the price is
                  worked out for you. Leave them and type the price, which is what a
                  fixed-price line has always been.
                </p>
              )}

              <div className="ie-total">
                <span>{isDoc ? 'Price' : 'Item total'}</span>
                {isDoc ? (
                  <div className="ie-money">
                    <span>$</span>
                    <input type="number" inputMode="decimal" min="0" step="0.01"
                      value={item.price ?? ''} onChange={e => onChange({ ...item, price: e.target.value })}
                      placeholder="0.00" />
                  </div>
                ) : (
                  <b>{fmtMoney(lineTotal)}</b>
                )}
              </div>
            </section>

            <div className="ie-actions">
              <button type="button" className="ie-delete" onClick={onDelete}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M4 7h16M9 7V4.5h6V7M6.5 7l1 13h9l1-13" /></svg>
                {t('ie.removeLine')}
              </button>
              <button type="button" className="ie-save" onClick={onClose}>
                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12.5l5.5 5.5L20 6.5" /></svg>
                Save line item
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="overlay active ie-overlay" onClick={onClose}></div>
    </>
  );
}
